import type { MapBounds } from '~~/shared/search-constants'
import { REGIONS, type RegionData } from '~~/shared/regions'
import departmentsData from '~~/shared/data/departments.json'

interface DepartmentBbox {
  code: string
  name: string
  slug: string
  region: string
  boundingBox: { north: number, south: number, east: number, west: number }
}

const DEPARTMENTS: DepartmentBbox[] = departmentsData as DepartmentBbox[]
const DEPT_BY_CODE = new Map(DEPARTMENTS.map(d => [d.code, d]))

/** Bbox approximative de la France métropolitaine (cohérente avec REGION_BBOX.metro). */
export const FRANCE_METRO_BBOX: MapBounds = {
  west: -5.5,
  south: 41,
  east: 10,
  north: 51.5,
}

export type MapContextLevel = 'france' | 'region' | 'department' | 'ambiguous'

export interface MapContext {
  level: MapContextLevel
  /** Code département (ex: '24') si level='department', sinon undefined */
  departmentCode?: string
  /** Slug région (ex: 'occitanie') si level='region' ou 'department', sinon undefined */
  regionSlug?: string
}

/* ============================================================
 * Helpers bbox
 * ============================================================ */

function bboxArea(b: MapBounds): number {
  return Math.max(0, b.east - b.west) * Math.max(0, b.north - b.south)
}

function bboxIntersection(a: MapBounds, b: MapBounds): MapBounds | null {
  const west = Math.max(a.west, b.west)
  const east = Math.min(a.east, b.east)
  const south = Math.max(a.south, b.south)
  const north = Math.min(a.north, b.north)
  if (west >= east || south >= north) return null
  return { west, east, south, north }
}

function bboxUnion(boxes: MapBounds[]): MapBounds | null {
  if (boxes.length === 0) return null
  return boxes.reduce((acc, b) => ({
    west: Math.min(acc.west, b.west),
    south: Math.min(acc.south, b.south),
    east: Math.max(acc.east, b.east),
    north: Math.max(acc.north, b.north),
  }))
}

function deptToBbox(d: DepartmentBbox): MapBounds {
  return { west: d.boundingBox.west, south: d.boundingBox.south, east: d.boundingBox.east, north: d.boundingBox.north }
}

/* ============================================================
 * API publique
 * ============================================================ */

/** Bbox d'une région = union des bbox de ses départements (calculé à la volée, mémoïsé). */
const _regionBboxCache = new Map<string, MapBounds>()

export function getRegionBbox(regionSlug: string): MapBounds | null {
  const cached = _regionBboxCache.get(regionSlug)
  if (cached) return cached

  const region = REGIONS.find(r => r.slug === regionSlug)
  if (!region) return null

  const deptBboxes = region.departments
    .map(d => DEPT_BY_CODE.get(d.code))
    .filter((d): d is DepartmentBbox => d !== undefined)
    .map(deptToBbox)

  const bbox = bboxUnion(deptBboxes)
  if (bbox) _regionBboxCache.set(regionSlug, bbox)
  return bbox
}

export function getDepartmentBbox(code: string): MapBounds | null {
  const dept = DEPT_BY_CODE.get(code)
  return dept ? deptToBbox(dept) : null
}

/**
 * Trouve le département dominant pour une viewport :
 * celui dont la surface d'intersection / surface viewport est la plus grande.
 * Retourne null si l'intersection majoritaire est < threshold (défaut 0.5).
 */
export function findDominantDepartment(
  viewport: MapBounds,
  threshold = 0.5,
): DepartmentBbox | null {
  const viewportArea = bboxArea(viewport)
  if (viewportArea === 0) return null

  let bestDept: DepartmentBbox | null = null
  let bestRatio = 0

  for (const dept of DEPARTMENTS) {
    const inter = bboxIntersection(viewport, deptToBbox(dept))
    if (!inter) continue
    const ratio = bboxArea(inter) / viewportArea
    if (ratio > bestRatio) {
      bestRatio = ratio
      bestDept = dept
    }
  }

  return bestRatio >= threshold ? bestDept : null
}

/**
 * Trouve la région dominante pour une viewport :
 * la région qui contient le plus de surface dans la viewport (somme des intersections
 * de ses départements). Retourne null si la dominance est < threshold (défaut 0.5).
 */
export function findDominantRegion(
  viewport: MapBounds,
  threshold = 0.5,
): RegionData | null {
  const viewportArea = bboxArea(viewport)
  if (viewportArea === 0) return null

  const ratiosByRegion = new Map<string, number>()
  for (const region of REGIONS) {
    let sum = 0
    for (const d of region.departments) {
      const dept = DEPT_BY_CODE.get(d.code)
      if (!dept) continue
      const inter = bboxIntersection(viewport, deptToBbox(dept))
      if (inter) sum += bboxArea(inter)
    }
    if (sum > 0) ratiosByRegion.set(region.slug, sum / viewportArea)
  }

  let bestSlug: string | null = null
  let bestRatio = 0
  for (const [slug, ratio] of ratiosByRegion) {
    if (ratio > bestRatio) {
      bestRatio = ratio
      bestSlug = slug
    }
  }

  if (!bestSlug || bestRatio < threshold) return null
  return REGIONS.find(r => r.slug === bestSlug) ?? null
}

/**
 * Détermine le niveau contextuel courant de la viewport.
 *
 * Heuristique :
 * - Si la viewport couvre >= 70% de la surface France métro → `france`
 * - Sinon, si un département domine (>= 60% de la viewport) → `department`
 * - Sinon, si une région domine (>= 60% de la viewport) → `region`
 * - Sinon → `ambiguous` (zoom intermédiaire à cheval sur plusieurs régions)
 */
export function getMapContext(viewport: MapBounds): MapContext {
  const viewportArea = bboxArea(viewport)
  if (viewportArea === 0) return { level: 'ambiguous' }

  // Niveau France : la viewport couvre une grande part de la France
  const interFrance = bboxIntersection(viewport, FRANCE_METRO_BBOX)
  const franceCoverage = interFrance
    ? bboxArea(interFrance) / bboxArea(FRANCE_METRO_BBOX)
    : 0
  if (franceCoverage >= 0.7) return { level: 'france' }

  // Niveau département (seuil 0.6 = la viewport est essentiellement dans 1 dept)
  const dept = findDominantDepartment(viewport, 0.6)
  if (dept) {
    const region = REGIONS.find(r =>
      r.departments.some(d => d.code === dept.code),
    )
    return {
      level: 'department',
      departmentCode: dept.code,
      regionSlug: region?.slug,
    }
  }

  // Niveau région
  const region = findDominantRegion(viewport, 0.6)
  if (region) return { level: 'region', regionSlug: region.slug }

  return { level: 'ambiguous' }
}

/**
 * Retourne le label affichable pour le bouton de dézoom d'un cran selon le contexte.
 * - department → propose dézoom vers la région parente ("↑ Occitanie")
 * - region → propose dézoom vers la France ("↑ France")
 * - france | ambiguous → null (rien à proposer)
 */
export function getZoomUpTarget(ctx: MapContext): { level: 'region' | 'france', label: string, bbox: MapBounds } | null {
  if (ctx.level === 'department' && ctx.regionSlug) {
    const region = REGIONS.find(r => r.slug === ctx.regionSlug)
    const bbox = getRegionBbox(ctx.regionSlug)
    if (region && bbox) return { level: 'region', label: region.name, bbox }
  }
  if (ctx.level === 'region') {
    return { level: 'france', label: 'France', bbox: FRANCE_METRO_BBOX }
  }
  return null
}
